import React from 'react';
import cx from 'classnames';
import MUIRadio, { RadioProps as MUIRadioProps } from '@material-ui/core/Radio';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import { makeStyles, useTheme } from '../styles';
import { Tooltip } from '@material-ui/core';

type RadioProps = {
  label: React.ReactNode;
  labelPlacement?: 'end' | 'start' | 'top' | 'bottom';
  value: string;
  checked?: boolean;
  disabled?: boolean;
  disabledText?: string;
  radioClassName?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  unfocusable?: boolean;
  radioColor?: string;
} & Pick<MUIRadioProps, 'className' | 'style' | 'onClick' | 'onFocus'>;

const useStyles = makeStyles(() => ({
  root: {
    marginLeft: 0,
    marginRight: 0,
  },
  label: {
    fontSize: 14,
    fontWeight: 400,
  },
  radioIconUnchecked: {
    stroke: '#777777',
    '&:hover': {
      stroke: '#808080',
    },
  },
}));

const Selected = ({ fill, disabled }: { fill: string; disabled?: boolean }) => {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="7.5" fill="white" stroke={disabled ? '#777777' : fill} />
      <circle cx="8" cy="8" r="5" fill={disabled ? '#777777' : fill} />
    </svg>
  );
};

const Unselected = () => {
  const classes = useStyles();
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="7.5" fill="#F8F8F8" className={classes.radioIconUnchecked} />
    </svg>
  );
};

const blur: React.FocusEventHandler<HTMLButtonElement> = e => e.target.blur();

export function Radio({
  value,
  label,
  labelPlacement = 'end',
  checked,
  disabled,
  className,
  radioClassName,
  style,
  disabledText = '',
  onChange,
  onFocus,
  /** Focusing on the radio can inhibit keyboard shortcuts */
  unfocusable = false,
  radioColor,
}: RadioProps) {
  const classes = useStyles();
  const theme = useTheme();

  return (
    <Tooltip title={!disabled ? '' : disabledText}>
      <FormControlLabel
        label={label}
        labelPlacement={labelPlacement}
        style={style}
        classes={{
          label: classes.label,
          root: cx(classes.root, className),
        }}
        control={
          <MUIRadio
            disableRipple
            color="primary"
            checkedIcon={
              <Selected disabled={disabled} fill={radioColor || theme.colors.periwinkle100} />
            }
            icon={<Unselected />}
            value={value}
            checked={checked}
            className={radioClassName}
            onChange={onChange}
            onFocus={unfocusable ? blur : onFocus}
          />
        }
        disabled={disabled}
      />
    </Tooltip>
  );
}

export default Radio;
